<template>
  <v-card flat color="transparent" class="mx-4" v-bind="$attrs">
    <div class="d-flex flex-wrap gap-2">
      <v-hover
        v-for="(archivo, index) in listado"
        :key="archivo.id"
        v-slot="{ isHovering, props }"
      >
        <v-card
          v-bind="props"
          :color="esImagen(archivo) ? '' : 'background'"
          height="80"
          width="80"
          class="d-flex align-center justify-center position-relative rounded overflow-hidden mx-2"
          flat
        >
          <!-- Contenido del archivo -->
          <template v-if="esImagen(archivo)">
            <v-img
              :src="rutaArchivo(archivo.archivo)"
              cover
              height="100%"
              width="100%"
            ></v-img>
          </template>
          <template v-else>
            <v-icon color="primary" size="40">mdi-file-pdf-box</v-icon>
          </template>

          <!-- Scrim y Botón de menú al hacer hover -->
          <v-overlay
            :model-value="isHovering || menus[archivo.id]"
            contained
            scrim="rgba(0,0,0,0.6)"
            class="align-center justify-center"
          >
            <div
              class="position-absolute"
              style="
                top: 0;
                right: 0;
                width: 100%;
                height: 100%;
                pointer-events: none;
              "
            >
              <div
                style="
                  position: absolute;
                  top: 8px;
                  right: 8px;
                  pointer-events: auto;
                "
              >
                <v-menu location="bottom end" v-model="menus[archivo.id]">
                  <template v-slot:activator="{ props }">
                    <v-btn
                      size="small"
                      color="secondary"
                      variant="flat"
                      v-bind="props"
                      class="rounded"
                      style="
                        min-width: 0;
                        padding: 0;
                        width: 32px;
                        height: 32px;
                      "
                    >
                      <v-icon color="white" size="large"
                        >mdi-dots-vertical</v-icon
                      >
                    </v-btn>
                  </template>
                  <v-list>
                    <!-- Opción: Ver -->
                    <v-list-item @click="verArchivo(archivo)" value="view">
                      <template v-slot:prepend>
                        <v-icon size="small" class="mr-2" color="secondary"
                          >mdi-eye</v-icon
                        >
                      </template>
                      <v-list-item-title>{{
                        t("boton-ver") || "View"
                      }}</v-list-item-title>
                    </v-list-item>

                    <!-- Opción: Descargar -->
                    <v-list-item
                      @click="descargarArchivo(archivo.archivo)"
                      value="download"
                    >
                      <template v-slot:prepend>
                        <v-icon size="small" class="mr-2" color="secondary"
                          >mdi-download</v-icon
                        >
                      </template>
                      <v-list-item-title>{{
                        t("boton-descargar") || "Download"
                      }}</v-list-item-title>
                    </v-list-item>

                    <!-- Opción: Eliminar -->
                    <v-list-item
                      v-if="borrar"
                      @click="borrarArchivo(archivo.id, index)"
                      value="delete"
                    >
                      <template v-slot:prepend>
                        <v-icon size="small" color="error" class="mr-2"
                          >mdi-delete</v-icon
                        >
                      </template>
                      <v-list-item-title class="text-error">{{
                        t("boton-eliminar") || "Delete"
                      }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </div>
          </v-overlay>
        </v-card>
      </v-hover>
    </div>
  </v-card>

  <!-- Diálogo de previsualización -->
  <v-dialog v-model="dialogoVer" max-width="900px">
    <v-card>
      <v-toolbar density="compact" color="white">
        <v-spacer></v-spacer>
        <v-btn icon @click="dialogoVer = false">
          <v-icon color="primary">mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text class="pa-0 d-flex justify-center bg-grey-lighten-3">
        <template v-if="archivoSeleccionado">
          <iframe
            v-if="!esImagen(archivoSeleccionado)"
            :src="rutaArchivo(archivoSeleccionado.archivo)"
            width="100%"
            height="600px"
            frameborder="0"
          ></iframe>
          <v-img
            v-else
            :src="rutaArchivo(archivoSeleccionado.archivo)"
            max-height="80vh"
            contain
          ></v-img>
        </template>
      </v-card-text>
      <v-card-actions class="justify-center" v-if="archivoSeleccionado">
        <v-btn
          color="primary"
          variant="outlined"
          @click="dialogoVer = false"
          class="mx-2"
        >
          {{ t("boton-cerrar") || "Cerrar" }}
        </v-btn>

        <v-btn
          color="primary"
          variant="flat"
          @click="descargarArchivo(archivoSeleccionado.archivo)"
          class="mx-2"
        >
          {{ t("boton-descargar") || "Download" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { defineProps, defineEmits, ref, reactive } from "vue";
import axios from "axios";
import { useI18n } from "vue-i18n";

// Deshabilitar la herencia automática de atributos
// ya que los manejamos explícitamente con $attrs
defineOptions({
  inheritAttrs: false,
});

// Inicializar i18n para traducciones (si están disponibles)
const { t } = useI18n();

const props = defineProps({
  listado: {
    type: Array,
    default: () => [],
  },
  titulo: String,
  tipo: String,
  borrar: {
    type: [Number, Boolean],
    default: 0,
  },
});

const emit = defineEmits(["update:listado"]);

// Estado para el diálogo de previsualización
const dialogoVer = ref(false);
const archivoSeleccionado = ref(null);
const menus = reactive({});

// Función auxiliar para determinar si es imagen
const esImagen = (archivo) => {
  if (!archivo || !archivo.archivo) return false;
  const ext = archivo.archivo.split(".").pop().toLowerCase();
  return ["jpg", "jpeg", "png", "gif", "webp"].includes(ext);
};

// Función para construir la ruta completa del archivo (ajustar según tu estructura de storage)
// Asumimos que 'descargar-archivo' retorna el archivo para descarga,
// pero para visualización necesitamos una URL accesible.
// Si tus archivos son privados y requieren token, puede ser necesario usar un endpoint que retorne el stream o un blob.
// Por ahora, usaremos la ruta relativa si es pública o el endpoint de descarga si el navegador puede manejarlo.
const rutaArchivo = (nombreArchivo) => {
  // NOTA: Esto asume que existe una ruta para visualizar o que el archivo es público.
  // Si es necesario usar el endpoint de descarga para ver el PDF en el iframe:
  return route("descargar-archivo", { archivo: nombreArchivo, ver: 1 });
};

const verArchivo = (archivo) => {
  archivoSeleccionado.value = archivo;
  dialogoVer.value = true;
};

const descargarArchivo = (archivo) => {
  window.location = route("descargar-archivo", {
    archivo: archivo,
  });
};

const borrarArchivo = (archivoId, index) => {
  axios.delete(route("stand-archivo.destroy", archivoId)).then(() => {
    const newList = [...props.listado];
    newList.splice(index, 1);
    emit("update:listado", newList);
  });
};
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}
</style>
